import {
  triggerToast,
  Assets,
  ButtonDS,
  SelectDS,
  AddressFormDS,
  ModalWithLeftPartDS,
  RowElement,
  CloseOutlined,
  TextCapitalized,
  Spacer,
  EditOutlined,
} from "@qivia/ui";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { selectRulesList } from "../../../rules/rulesSlice";
import { selectListAllVehiclesInDb } from "../../../vehicles/vehiclesSlice";
import {
  CardsListDisplayed,
  CardsListType,
  ModalVehicleType,
} from "../../../cardsAndBadges/cards/cardsAPI";
import {
  selectCardCreationStatus,
  selectCardsList,
  cardsAsync,
  selectCompanyAddress,
  cardsSlice,
} from "../../../cardsAndBadges/cards/cardsSlice";
import { selectCompany } from "../../../homeSlice";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import "../../../../../i18n/i18n";
import { AddressInput } from "../../cards/sharedInterface";

const findVehiclesNotAssignedToCard = (
  vehicles: ModalVehicleType[],
  cardsList: CardsListType[] | CardsListDisplayed[],
) => {
  return vehicles.filter(
    (vehicle) =>
      cardsList.find(
        (card) =>
          ((vehicle.registrationNumber &&
            card.registrationNumber === vehicle.registrationNumber) ||
            (vehicle.reference &&
              card.vehicleReference === vehicle.reference) ||
            vehicle.status === "DEACTIVATED") &&
          card.status !== "IN_OPPOSITION",
      ) === undefined,
  );
};

export const AddCardModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { onCloseModal } = props;

  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [hasAddressCreated, setHasAddressCreated] = useState<boolean>(false);
  const vehicles = useAppSelector(selectListAllVehiclesInDb);
  const rules = useAppSelector(selectRulesList);
  const company = useAppSelector(selectCompany);
  const cardCreationStatus = useAppSelector(selectCardCreationStatus);
  const companyAddress = useAppSelector(selectCompanyAddress);

  const cardsList: CardsListDisplayed[] = useAppSelector(selectCardsList).map(
    ({
      status,
      last4Digits,
      registrationNumber,
      ruleName,
      vehicleReference,
    }) => ({
      status,
      registrationNumber,
      last4Digits,
      rule: ruleName,
      vehicleReference,
    }),
  );

  const addressInitValues = useMemo(() => {
    return {
      addressLine1: "",
      addressLine2: "",
      city: "",
      postalCode: "",
      country: "FR",
    };
  }, []);
  const [isValidAddressForm, setIsValidAddressForm] = useState(false);
  const [vehicle, setVehicle] = useState<string>("");
  const [rule, setRule] = useState<string>("");
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [addressInput, setAddressInput] =
    useState<AddressInput>(addressInitValues);

  const vehiclesNotAssignedToCard: ModalVehicleType[] =
    findVehiclesNotAssignedToCard(vehicles, cardsList);

  const optionVehicle = useMemo(
    () =>
      vehiclesNotAssignedToCard.map((vehicle) => {
        return {
          value: vehicle.uuid,
          label: [vehicle.registrationNumber, vehicle.reference]
            .filter((d) => d)
            .join(" - "),
        };
      }),
    [vehiclesNotAssignedToCard],
  );

  const optionRule = useMemo(
    () =>
      rules.map((rule) => {
        return {
          value: rule.uuid,
          label: rule.name,
        };
      }),
    [rules],
  );

  const valueOptionVehicle = useMemo(
    () => optionVehicle.find((option) => option.value === vehicle),
    [optionVehicle, vehicle],
  );
  const valueOptionRule = useMemo(
    () => optionRule.find((option) => option.value === rule),
    [optionRule, rule],
  );

  useEffect(() => {
    if (cardCreationStatus === "success") {
      triggerToast(t("cards.creation.success") || "", "valid");
      dispatch(cardsSlice.actions.resetCardCreationStatus());
    } else if (cardCreationStatus === "failed") {
      triggerToast(t("cards.creation.failure") || "", "error");
      dispatch(cardsSlice.actions.resetCardCreationStatus());
    }
  }, [cardCreationStatus, t, dispatch]);

  useEffect(() => {
    if (vehicle && rule && isValidAddressForm) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [isValidAddressForm, rule, vehicle]);

  useEffect(() => {
    hasAddressCreated &&
    (addressInput.addressLine1 === "" ||
      addressInput.city === "" ||
      addressInput.postalCode === "")
      ? setIsValidAddressForm(false)
      : setIsValidAddressForm(true);
  }, [addressInput, hasAddressCreated]);

  const resetValue = useCallback(() => {
    setAddressInput(addressInitValues);
    setRule("");
    setVehicle("");
    setDisplayError(false);
    setHasAddressCreated(false);
  }, [addressInitValues]);

  const onSubmit = useCallback(() => {
    if (!company) {
      return;
    }
    const cardData = {
      vehicle: vehicle,
      rule: rule,
      companyUuid: company.uuid,
      address: hasAddressCreated ? addressInput : null,
    };
    void dispatch(cardsAsync(cardData));
    resetValue();
    onCloseModal();
  }, [
    addressInput,
    company,
    dispatch,
    hasAddressCreated,
    onCloseModal,
    resetValue,
    rule,
    vehicle,
  ]);

  const updateAddress = useCallback(
    (key: keyof AddressInput, value: string) => {
      const newValueState = {
        ...addressInput,
        [key]: value,
      };
      setAddressInput(newValueState);
    },
    [addressInput],
  );
  const clickToSubmit = useCallback(() => {
    setDisplayError(true);
    if (!buttonDisabled) {
      onSubmit();
    }
  }, [buttonDisabled, onSubmit]);

  if (!company) {
    return;
  }

  return (
    <ModalWithLeftPartDS
      title={t("cards.modal.title") || ""}
      visible={props.visible}
      tagText={t("cards.modal.tag") || ""}
      onClose={() => {
        resetValue();
        props.onCloseModal();
      }}
      leftPart={{
        button: (
          <ButtonDS
            text={t("cards.modal.add") || ""}
            onClick={clickToSubmit}
            disabled={buttonDisabled}
            format="fill"
            buttonType="primary"
          />
        ),
        icon: <Assets.QiviaCardUnlocked />,
      }}
    >
      <SelectDS
        label={t("cards.modal.vehicle") || ""}
        placeholder={t("cards.modal.vehicle.placeholder") || ""}
        value={valueOptionVehicle}
        options={optionVehicle}
        onChange={(selectedOption) =>
          setVehicle(selectedOption ? selectedOption.value : "")
        }
        error={
          (displayError &&
            vehicle === "" &&
            t("cards.modal.error.selectInput")) ||
          undefined
        }
        allWidth
      />
      <SelectDS
        label={t("cards.modal.rule") || ""}
        placeholder={t("cards.modal.rule.placeholder") || ""}
        value={valueOptionRule}
        options={optionRule}
        onChange={(selectedOption) =>
          setRule(selectedOption ? selectedOption.value : "")
        }
        error={
          (displayError && rule === "" && t("cards.modal.error.selectInput")) ||
          undefined
        }
        allWidth
      />
      {hasAddressCreated ? (
        <StyledFlex>
          <AddressFormDS
            label={t("address.label.delivery") || ""}
            addressForm={addressInput}
            error={displayError}
            onChange={(key, value) => updateAddress(key, value)}
            onKeyDown={(key) => key === "Enter" && clickToSubmit()}
            // hasName
          />
          <StyledClose>
            <CloseOutlined onClick={() => setHasAddressCreated(false)} />
          </StyledClose>
        </StyledFlex>
      ) : (
        <>
          <StyledTitle>
            <Spacer x={0.2} />
            <TextCapitalized>{t(`badges.modal.address`)}</TextCapitalized>
          </StyledTitle>
          <Spacer y={1} />
          <RowElement
            text={company.name}
            subText={companyAddress}
            size={"L"}
            rightElement={
              <StyledElement>
                <EditOutlined onClick={() => setHasAddressCreated(true)} />
              </StyledElement>
            }
          />
        </>
      )}
    </ModalWithLeftPartDS>
  );
};

const StyledFlex = styled.div`
  display: flex;
`;

const StyledClose = styled.div`
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledTitle = styled.div`
  display: flex;
  width: 100%;
  ${typographies["Body/M"]};
`;

const StyledElement = styled.div`
  cursor: pointer;
  & svg {
    height: 1.2rem;
  }
`;
